import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Download, LogOut, Search, RefreshCw, FileText, Tablet, Image as ImgIcon, Save, Trash2, Upload, KeyRound, Building2, Copy, Sparkles, Trash, QrCode, Printer, FileUp, History, Users, ShieldAlert, UserCog, UserX, AlertTriangle, CheckCircle2, FileSignature, ShieldCheck, Plus, Clock, Ban, Smartphone, Type } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { docTextStyle, DEFAULT_DISPLAY } from "@/lib/displayStyle";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { SepaSwitchDialog } from "@/components/SepaSwitchDialog";

export default function AdminDashboard() {
  const { user, logout, authHeaders, refresh } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("consents");

  // consents
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [loadingC, setLoadingC] = useState(false);

  // privacy
  const [policyText, setPolicyText] = useState("");
  const [policyVersion, setPolicyVersion] = useState("");
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [privacyQuestions, setPrivacyQuestions] = useState([]);
  const [savingQuestions, setSavingQuestions] = useState(false);
  const [contractText, setContractText] = useState("");
  const [contractVersion, setContractVersion] = useState("");
  const [savingContract, setSavingContract] = useState(false);
  const [importingContractPdf, setImportingContractPdf] = useState(false);
  const contractPdfRef = useRef(null);

  // settings
  const [practice, setPractice] = useState({ name: "", address: "", logo_filename: null, code: "" });
  const [displaySettings, setDisplaySettings] = useState(DEFAULT_DISPLAY);
  const [savingDisplay, setSavingDisplay] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState("privacy");
  const [pwCur, setPwCur] = useState(""); const [pwNew, setPwNew] = useState("");
  const [logoTick, setLogoTick] = useState(0);
  const fileRef = useRef(null);
  const policyPdfRef = useRef(null);
  const [importingPdf, setImportingPdf] = useState(false);

  // audit log
  const [auditRows, setAuditRows] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [auditFilter, setAuditFilter] = useState("");

  // staff
  const [staffRows, setStaffRows] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [newStaff, setNewStaff] = useState({ email: "", name: "", role: "staff", password: "" });
  const [savingStaff, setSavingStaff] = useState(false);

  // PDF disclaimer modal
  const [pdfDialog, setPdfDialog] = useState({ open: false, id: null, name: "", hasTreatment: false });

  // delete consent modal
  const [delDialog, setDelDialog] = useState({ open: false, id: null, name: "", category: "patient_request", reason: "" });
  const [deleting, setDeleting] = useState(false);

  // trash (Papierkorb)
  const [trashRows, setTrashRows] = useState([]);
  const [loadingTrash, setLoadingTrash] = useState(false);

  // Abo / Zahlung
  const [plan, setPlan] = useState(null);
  const [sepaOpen, setSepaOpen] = useState(false);

  const isAdmin = user && user !== false && user.role === "practice_admin";
  const isStaff = user && user !== false && user.role === "staff";
  const isPaying = practice.subscription_status === "active";
  const fmtEuro = (n) => Number(n).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  useEffect(() => {
    if (user === false) navigate("/login", { replace: true });
    if (user && user.role === "superadmin") navigate("/super", { replace: true });
    if (user && user !== false && user.access === "payment_required") navigate("/bezahlen", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (user && user !== false && (user.role === "practice_admin" || user.role === "staff")) {
      loadConsents();
      if (user.role === "practice_admin") { loadPolicy(); loadContract(); loadPractice(); api.get("/payments/plan").then(({ data }) => setPlan(data)).catch(() => {}); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadConsents = async (q = "", silent = false) => {
    if (!silent) setLoadingC(true);
    try {
      const { data } = await api.get("/admin/consents", { params: q ? { q } : {}, headers: authHeaders });
      setRows(data);
    } catch (e) {
      if (e.response?.status === 401) navigate("/login", { replace: true });
      else if (!silent) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler beim Laden.");
    } finally { if (!silent) setLoadingC(false); }
  };

  // Auto-Refresh alle 10 s im Einwilligungen-Tab (sichtbarer, nicht-störender Background-Poll)
  useEffect(() => {
    if (!isAdmin && !isStaff) return undefined;
    if (tab !== "consents") return undefined;
    const iv = setInterval(() => { loadConsents(query, true); }, 10000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, query, user]);

  const loadPolicy = async () => {
    try {
      const { data } = await api.get("/admin/privacy-policy", { headers: authHeaders });
      setPolicyText(data.text); setPolicyVersion(data.version);
      setPrivacyQuestions(Array.isArray(data.questions) ? data.questions : []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addQuestion = () => setPrivacyQuestions((qs) => [...qs, { id: null, text: "" }]);
  const updateQuestion = (idx, text) => setPrivacyQuestions((qs) => qs.map((q, i) => (i === idx ? { ...q, text } : q)));
  const removeQuestion = (idx) => setPrivacyQuestions((qs) => qs.filter((_, i) => i !== idx));

  const saveQuestions = async () => {
    const cleaned = privacyQuestions.map((q) => ({ id: q.id || undefined, text: (q.text || "").trim() })).filter((q) => q.text.length > 0);
    setSavingQuestions(true);
    try {
      const { data } = await api.put("/admin/privacy-policy/questions", { questions: cleaned }, { headers: authHeaders });
      setPrivacyQuestions(Array.isArray(data.questions) ? data.questions : []);
      toast.success("Zusatzfragen gespeichert.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen."); }
    finally { setSavingQuestions(false); }
  };

  const loadPractice = async () => {
    try {
      const { data } = await api.get("/admin/practice", { headers: authHeaders });
      setPractice({
        name: data.name, address: data.address || "", logo_filename: data.logo_filename, code: data.code || "",
        trial_ends_at: data.trial_ends_at, cancelled_at: data.cancelled_at,
        export_deadline: data.export_deadline, cancelled_by: data.cancelled_by,
        subscription_status: data.subscription_status, paid_until: data.paid_until,
        billing_method: data.billing_method, sepa_info: data.sepa_info, first_paid_at: data.first_paid_at,
      });
      setDisplaySettings({ ...DEFAULT_DISPLAY, ...(data.display_settings || {}) });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const exportMyData = async () => {
    try {
      toast.info("Export wird erstellt …");
      const res = await api.get("/admin/practice/export", { headers: authHeaders, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `export_${(practice.name || "praxis").replace(/[^A-Za-z0-9_-]+/g, "_")}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Datenexport heruntergeladen.");
    } catch { toast.error("Export fehlgeschlagen."); }
  };

  const cancelMyPractice = async () => {
    const ok = window.confirm(
      "Praxis löschen / Abo beenden?\n\n" +
      "Ihr Zugang bleibt anschließend noch 30 Tage bestehen, damit Sie Ihre Daten exportieren und sichern können. " +
      "Nach Ablauf dieser Frist wird der Zugang gesperrt.\n\nMöchten Sie fortfahren?"
    );
    if (!ok) return;
    try {
      const { data } = await api.post("/admin/practice/cancel", {}, { headers: authHeaders });
      const dl = new Date(data.export_deadline).toLocaleDateString("de-DE");
      toast.success(`Kündigung erfasst. Sie können Ihre Daten bis ${dl} (${data.grace_days} Tage) exportieren.`);
      loadPractice();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const regenerateCode = async () => {
    if (!window.confirm("Neuen Code generieren? Der alte Code funktioniert dann nicht mehr.")) return;
    try {
      const { data } = await api.post("/admin/practice/regenerate-code", {}, { headers: authHeaders });
      setPractice((p) => ({ ...p, code: data.code }));
      toast.success("Neuer Code erzeugt.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(practice.code); toast.success("Code kopiert."); }
    catch { toast.error("Kopieren nicht möglich."); }
  };

  const qrTargetUrl = practice.code ? `${window.location.origin}/p/${practice.code}` : "";

  const downloadQrPng = async () => {
    const svg = document.getElementById("practice-qr-svg");
    if (!svg) { toast.error("QR-Code nicht bereit."); return; }
    const xml = new XMLSerializer().serializeToString(svg);
    const svg64 = btoa(unescape(encodeURIComponent(xml)));
    const img = new Image();
    img.onload = () => {
      const size = 1024;
      const canvas = document.createElement("canvas");
      canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `praxis-qr-${practice.code}.png`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }, "image/png");
    };
    img.src = `data:image/svg+xml;base64,${svg64}`;
  };

  const printQrPoster = () => {
    if (!practice.code) return;
    const w = window.open("", "_blank", "noopener,noreferrer,width=900,height=1200");
    if (!w) { toast.error("Pop-up blockiert. Bitte erlauben Sie Pop-ups."); return; }
    const safeName = (practice.name || "Praxis").replace(/[<>]/g, "");
    w.document.write(`<!doctype html><html lang="de"><head><meta charset="utf-8"><title>QR-Aufsteller — ${safeName}</title>
      <style>
        @page { size: A4; margin: 18mm; }
        html, body { margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #1c1917; }
        .poster { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; padding: 24mm 16mm; box-sizing: border-box; }
        .brand { font-size: 14pt; letter-spacing: 0.18em; text-transform: uppercase; color: #57534e; margin-bottom: 4mm; }
        h1 { font-size: 32pt; font-weight: 600; margin: 0 0 6mm; text-align: center; color: #064e3b; line-height: 1.15; }
        .lead { font-size: 14pt; text-align: center; color: #44403c; max-width: 140mm; margin: 0 0 14mm; }
        .qr-wrap { padding: 10mm; border: 2px solid #064e3b; border-radius: 12mm; background: #fff; }
        .step { margin-top: 14mm; font-size: 12pt; color: #44403c; text-align: center; max-width: 140mm; }
        .code { margin-top: 8mm; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 14pt; letter-spacing: 0.35em; color: #064e3b; }
        .foot { margin-top: auto; font-size: 9pt; color: #a8a29e; }
      </style></head><body>
      <div class="poster">
        <div class="brand">Datenschutz-Einwilligung</div>
        <h1>${safeName}</h1>
        <p class="lead">Bitte scannen Sie den QR-Code mit Ihrer Handy-Kamera, um die DSGVO-Einwilligung digital zu erteilen.</p>
        <div class="qr-wrap" id="qrslot"></div>
        <p class="step">1. Kamera öffnen &nbsp;·&nbsp; 2. QR scannen &nbsp;·&nbsp; 3. Formular ausfüllen &amp; unterschreiben</p>
        <div class="code">Praxis-Code: ${practice.code}</div>
        <p class="foot">DSGVO-konform · Verarbeitung ausschließlich bei ${safeName}</p>
      </div>
      <script>window.addEventListener('load',()=>setTimeout(()=>window.print(),250));<\/script>
      </body></html>`);
    const svg = document.getElementById("practice-qr-svg");
    if (svg) {
      const clone = svg.cloneNode(true);
      clone.setAttribute("width", "260");
      clone.setAttribute("height", "260");
      w.document.getElementById("qrslot")?.appendChild(clone);
    }
    w.document.close();
  };

  const savePolicy = async () => {
    setSavingPolicy(true);
    try {
      const { data } = await api.put("/admin/privacy-policy", { text: policyText }, { headers: authHeaders });
      setPolicyVersion(data.version);
      toast.success(`Neue Version ${data.version} gespeichert.`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingPolicy(false); }
  };

  const importPolicyPdf = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Bitte eine PDF-Datei auswählen."); return; }
    const fd = new FormData(); fd.append("file", file);
    setImportingPdf(true);
    try {
      const { data } = await api.post("/admin/privacy-policy/import-pdf", fd,
        { headers: { ...authHeaders, "Content-Type": "multipart/form-data" } });
      setPolicyText(data.text);
      toast.success(`PDF importiert (${data.pages} Seite${data.pages === 1 ? "" : "n"}, ${data.chars} Zeichen). Bitte prüfen und speichern.`);
    } catch (e) {
      const status = e.response?.status;
      if (status === 422) toast.error("Aus der PDF konnte kein Text extrahiert werden. Eingescannte PDFs ohne OCR werden nicht unterstützt.");
      else toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    finally { setImportingPdf(false); if (policyPdfRef.current) policyPdfRef.current.value = ""; }
  };

  const loadContract = async () => {
    try {
      const { data } = await api.get("/admin/treatment-contract", { headers: authHeaders });
      setContractText(data.text || "");
      setContractVersion(data.version || "");
    } catch (_e) {}
  };

  const saveContract = async () => {
    if (!contractText || contractText.trim().length < 10) {
      toast.error("Bitte einen Behandlungsvertrag mit mindestens 10 Zeichen eingeben."); return;
    }
    setSavingContract(true);
    try {
      const { data } = await api.put("/admin/treatment-contract", { text: contractText }, { headers: authHeaders });
      setContractVersion(data.version);
      toast.success(`Behandlungsvertrag Version ${data.version} gespeichert.`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingContract(false); }
  };

  const importContractPdf = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Bitte eine PDF-Datei auswählen."); return; }
    const fd = new FormData(); fd.append("file", file);
    setImportingContractPdf(true);
    try {
      const { data } = await api.post("/admin/treatment-contract/import-pdf", fd,
        { headers: { ...authHeaders, "Content-Type": "multipart/form-data" } });
      setContractText(data.text);
      toast.success(`PDF importiert (${data.pages} Seite${data.pages === 1 ? "" : "n"}, ${data.chars} Zeichen). Bitte prüfen und speichern.`);
    } catch (e) {
      const status = e.response?.status;
      if (status === 422) toast.error("Aus der PDF konnte kein Text extrahiert werden. Eingescannte PDFs ohne OCR werden nicht unterstützt.");
      else toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    finally { setImportingContractPdf(false); if (contractPdfRef.current) contractPdfRef.current.value = ""; }
  };


  const loadAudit = async (filter = "") => {
    setLoadingAudit(true);
    try {
      const params = filter ? { action: filter } : {};
      const { data } = await api.get("/admin/audit-log", { params, headers: authHeaders });
      setAuditRows(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoadingAudit(false); }
  };

  const loadStaff = async () => {
    setLoadingStaff(true);
    try {
      const { data } = await api.get("/admin/staff", { headers: authHeaders });
      setStaffRows(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoadingStaff(false); }
  };

  const createStaff = async () => {
    if (!newStaff.email || !newStaff.name || newStaff.password.length < 8) {
      toast.error("Bitte E-Mail, Name und Passwort (min. 8 Zeichen) angeben."); return;
    }
    setSavingStaff(true);
    try {
      await api.post("/admin/staff", newStaff, { headers: authHeaders });
      toast.success(`Mitarbeiter "${newStaff.name}" angelegt.`);
      setNewStaff({ email: "", name: "", role: "staff", password: "" });
      loadStaff();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingStaff(false); }
  };

  const updateStaffRole = async (sid, role) => {
    try { await api.patch(`/admin/staff/${sid}`, { role }, { headers: authHeaders }); loadStaff(); toast.success("Rolle aktualisiert."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleStaffActive = async (sid, active) => {
    try { await api.patch(`/admin/staff/${sid}`, { active }, { headers: authHeaders }); loadStaff(); toast.success(active ? "Aktiviert." : "Deaktiviert."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const resetStaffPassword = async (sid, email) => {
    const pw = window.prompt(`Neues Passwort für ${email} setzen (min. 8 Zeichen):`);
    if (!pw || pw.length < 8) return;
    try { await api.post(`/admin/staff/${sid}/reset-password`, { new_password: pw }, { headers: authHeaders }); toast.success("Passwort zurückgesetzt."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteStaff = async (sid, email) => {
    if (!window.confirm(`Mitarbeiter ${email} entfernen?`)) return;
    try { await api.delete(`/admin/staff/${sid}`, { headers: authHeaders }); loadStaff(); toast.success("Mitarbeiter entfernt."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const switchUser = async () => { await logout(); navigate("/login", { replace: true }); };

  const loadTrash = async () => {
    setLoadingTrash(true);
    try {
      const { data } = await api.get("/admin/consents/trash", { headers: authHeaders });
      setTrashRows(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler beim Laden des Papierkorbs."); }
    finally { setLoadingTrash(false); }
  };

  const restoreConsent = async (id, name) => {
    if (!window.confirm(`Einwilligung von "${name}" wiederherstellen?`)) return;
    try {
      await api.post(`/admin/consents/${id}/restore`, {}, { headers: authHeaders });
      toast.success("Einwilligung wiederhergestellt.");
      loadTrash();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Wiederherstellung fehlgeschlagen."); }
  };

  useEffect(() => {
    if (tab === "audit" && isAdmin) loadAudit(auditFilter);
    if (tab === "staff" && isAdmin) loadStaff();
    if (tab === "trash" && isAdmin) loadTrash();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post("/admin/logo", fd, { headers: { ...authHeaders, "Content-Type": "multipart/form-data" } });
      toast.success("Logo hochgeladen.");
      setLogoTick(Date.now()); loadPractice(); refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const removeLogo = async () => {
    try {
      await api.delete("/admin/logo", { headers: authHeaders });
      toast.success("Logo entfernt."); setLogoTick(Date.now()); loadPractice(); refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const savePractice = async () => {
    try {
      await api.put("/admin/practice", { name: practice.name, address: practice.address }, { headers: authHeaders });
      toast.success("Praxisdaten aktualisiert."); refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveDisplaySettings = async () => {
    setSavingDisplay(true);
    try {
      await api.put("/admin/practice",
        { name: practice.name || "Praxis", address: practice.address, display_settings: displaySettings },
        { headers: authHeaders });
      toast.success("Lesbarkeits-Einstellungen gespeichert.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingDisplay(false); }
  };

  const changePassword = async () => {
    if (pwNew.length < 8) { toast.error("Neues Passwort min. 8 Zeichen."); return; }
    try {
      await api.post("/admin/change-password", { current_password: pwCur, new_password: pwNew }, { headers: authHeaders });
      toast.success("Passwort geändert."); setPwCur(""); setPwNew("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const startKiosk = async () => {
    try {
      await api.post("/admin/kiosk/start", {}, { headers: authHeaders });
      // Admin token ist jetzt server-seitig durch kiosk_token ersetzt.
      // Lokales Bearer-Token entfernen, damit Patient nicht zurück ins Admin kann.
      localStorage.removeItem("auth_token");
      window.location.assign("/patient");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const downloadPdf = async (id, name, part = "combined", ack = false) => {
    try {
      const url = part === "combined" ? `/admin/consents/${id}/pdf` : `/admin/consents/${id}/pdf/${part}`;
      const res = await api.get(url, {
        headers: authHeaders, responseType: "blob", params: { ack: ack ? 1 : 0 },
      });
      const blobUrl = window.URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = blobUrl;
      const safe = name.replace(/\s+/g, "_");
      const prefix = part === "privacy" ? "datenschutz" : part === "treatment" ? "behandlungsvertrag" : "einwilligung";
      a.download = `${prefix}_${safe}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(blobUrl);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const askDownloadPdf = (id, name, hasTreatment) => setPdfDialog({ open: true, id, name, hasTreatment: !!hasTreatment });
  const confirmDownloadPdf = async (part = "combined") => {
    const { id, name } = pdfDialog;
    setPdfDialog({ open: false, id: null, name: "", hasTreatment: false });
    if (id) await downloadPdf(id, name, part, true);
  };

  const askDeleteConsent = (id, name) => setDelDialog({ open: true, id, name, category: "patient_request", reason: "" });
  const confirmDeleteConsent = async () => {
    const { id, category, reason } = delDialog;
    if (!id || reason.trim().length < 3) { toast.error("Bitte Begründung mit mindestens 3 Zeichen angeben."); return; }
    setDeleting(true);
    try {
      await api.delete(`/admin/consents/${id}`, { headers: authHeaders, data: { reason: reason.trim(), category } });
      toast.success("Einwilligung gelöscht (Soft-Delete, im Verlauf dokumentiert).");
      setDelDialog({ open: false, id: null, name: "", category: "patient_request", reason: "" });
      loadConsents(query);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setDeleting(false); }
  };

  if (!user || user === false) return <div className="min-h-screen grid place-items-center text-stone-400">Lädt…</div>;

  const logoUrl = practice.logo_filename ? `${API}/admin/logo?t=${logoTick}` : null;

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="max-w-6xl mx-auto px-6 md:px-10 pt-8 pb-2 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-900 text-white grid place-items-center">
            <Building2 className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-stone-500">{user.practice?.name}</div>
            <div className="font-display text-lg font-semibold text-emerald-900">
              {isStaff ? "Mitarbeiter-Bereich" : "Admin-Bereich"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div data-testid="active-user-badge"
            className="hidden sm:flex items-center gap-2 px-3 h-12 rounded-xl bg-white border border-stone-200 text-stone-800">
            <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-900 grid place-items-center text-xs font-semibold">
              {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="leading-tight">
              <div className="text-sm font-medium">{user.name || user.email}</div>
              <div className="text-[10px] uppercase tracking-wider text-stone-500">
                {user.role === "practice_admin" ? "Praxis-Admin" : "Mitarbeiter"}
              </div>
            </div>
          </div>
          <Button data-testid="switch-user-button" variant="ghost" onClick={switchUser}
            className="h-12 rounded-xl text-stone-700 hover:bg-stone-100">
            <UserCog className="mr-2 w-4 h-4" /> Benutzer wechseln
          </Button>
          <Button data-testid="kiosk-start-button" onClick={startKiosk}
            className="h-12 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white">
            <Tablet className="mr-2 w-4 h-4" /> Patientenmodus starten
          </Button>
          <Button data-testid="admin-logout-button" variant="secondary"
            onClick={async () => { await logout(); navigate("/login", { replace: true }); }}
            className="h-12 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-900">
            <LogOut className="mr-2 w-4 h-4" /> Abmelden
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-6">
        {practice.trial_ends_at && !isPaying && practice.billing_method !== "directdebit" && (() => {
          const end = new Date(practice.trial_ends_at);
          const days = Math.ceil((end - new Date()) / 86400000);
          const expired = days < 0;
          return (
            <div data-testid="trial-banner"
              className={`mb-6 rounded-2xl border px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 ${expired ? "bg-orange-50 border-orange-200" : "bg-emerald-50 border-emerald-100"}`}>
              <Clock className={`w-5 h-5 shrink-0 ${expired ? "text-orange-600" : "text-emerald-700"}`} />
              <p className={`text-sm flex-1 ${expired ? "text-orange-800" : "text-emerald-900"}`}>
                {expired
                  ? <>Ihr Testzeitraum ist <strong>abgelaufen</strong>. Schließen Sie jetzt Ihr Abo ab, um LAIzyDocu weiter zu nutzen.</>
                  : <>Sie befinden sich im <strong>Testzeitraum</strong> – noch <strong>{days} Tag{days === 1 ? "" : "e"}</strong> (bis {end.toLocaleDateString("de-DE")}). Sichern Sie sich jetzt dauerhaften Zugang.</>}
              </p>
              <Button data-testid="trial-upgrade-button" onClick={() => navigate("/bezahlen")}
                className="h-11 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white shrink-0">
                <Sparkles className="mr-2 w-4 h-4" /> Jetzt Abo abschließen
              </Button>
            </div>
          );
        })()}
        {practice.cancelled_at && practice.export_deadline && (() => {
          const end = new Date(practice.export_deadline);
          const days = Math.ceil((end - new Date()) / 86400000);
          const expired = days < 0;
          return (
            <div data-testid="cancellation-banner"
              className="mb-6 rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <AlertTriangle className="w-5 h-5 shrink-0 text-rose-600" />
              <p className="text-sm text-rose-900 flex-1">
                {expired
                  ? <>Die Frist zur Datensicherung ist <strong>abgelaufen</strong>. Bitte kontaktieren Sie uns.</>
                  : <>Ihr Abo ist <strong>gekündigt</strong>. Sie haben noch <strong>{days} Tag{days === 1 ? "" : "e"}</strong> (bis <strong>{end.toLocaleDateString("de-DE")}</strong>), um Ihre Daten zu sichern. Danach wird der Zugang gesperrt.</>}
              </p>
              <Button data-testid="cancellation-banner-export" onClick={exportMyData}
                className="h-11 rounded-xl bg-rose-600 hover:bg-rose-700 text-white shrink-0">
                <Download className="mr-2 w-4 h-4" /> Daten exportieren
              </Button>
            </div>
          );
        })()}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-auto bg-white border border-stone-100 rounded-xl p-1.5 mb-6 flex-wrap justify-start gap-1.5">
            <TabsTrigger value="consents" data-testid="tab-consents" className="px-5 h-10 rounded-lg data-[state=active]:bg-emerald-900 data-[state=active]:text-white">Einwilligungen</TabsTrigger>
            {isAdmin && <TabsTrigger value="privacy" data-testid="tab-privacy" className="px-5 h-10 rounded-lg data-[state=active]:bg-emerald-900 data-[state=active]:text-white">Datenschutztext</TabsTrigger>}
            {isAdmin && <TabsTrigger value="contract" data-testid="tab-contract" className="px-5 h-10 rounded-lg data-[state=active]:bg-emerald-900 data-[state=active]:text-white">Behandlungsvertrag</TabsTrigger>}
            {isAdmin && <TabsTrigger value="staff" data-testid="tab-staff" className="px-5 h-10 rounded-lg data-[state=active]:bg-emerald-900 data-[state=active]:text-white">Mitarbeiter</TabsTrigger>}
            {isAdmin && <TabsTrigger value="settings" data-testid="tab-settings" className="px-5 h-10 rounded-lg data-[state=active]:bg-emerald-900 data-[state=active]:text-white">Einstellungen</TabsTrigger>}
            {isAdmin && <TabsTrigger value="audit" data-testid="tab-audit" className="px-5 h-10 rounded-lg data-[state=active]:bg-emerald-900 data-[state=active]:text-white">Verlauf</TabsTrigger>}
            {isAdmin && <TabsTrigger value="trash" data-testid="tab-trash" className="px-5 h-10 rounded-lg data-[state=active]:bg-emerald-900 data-[state=active]:text-white">Papierkorb</TabsTrigger>}
          </TabsList>

          <TabsContent value="consents">
            <div className="bg-white rounded-3xl border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 md:p-8">
              <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-6">
                <h1 className="font-display text-2xl font-semibold text-emerald-900">Alle Einwilligungen</h1>
                <div className="flex gap-2 items-center">
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input data-testid="admin-search-input" placeholder="Nach Name suchen…" value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && loadConsents(query)}
                      className="h-12 pl-11 pr-4 rounded-xl w-72" />
                  </div>
                  <Button data-testid="admin-search-button" onClick={() => loadConsents(query)}
                    className="h-12 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white">Suchen</Button>
                  <Button variant="secondary" onClick={() => { setQuery(""); loadConsents(""); }}
                    className="h-12 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-900">
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="rounded-2xl border border-stone-100 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-stone-50 hover:bg-stone-50">
                      <TableHead className="py-4 text-stone-600 w-12">Status</TableHead>
                      <TableHead className="py-4 text-stone-600">Name</TableHead>
                      <TableHead className="py-4 text-stone-600">E-Mail</TableHead>
                      <TableHead className="py-4 text-stone-600">Zeitpunkt</TableHead>
                      <TableHead className="py-4 text-stone-600">Dokumente</TableHead>
                      <TableHead className="py-4 text-stone-600 text-right">PDF</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingC && <TableRow><TableCell colSpan={6} className="text-center py-12 text-stone-400">Lädt…</TableCell></TableRow>}
                    {!loadingC && rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-12 text-stone-400">Keine Einträge.</TableCell></TableRow>}
                    {rows.map((r) => {
                      const isFresh = (Date.now() - new Date(r.timestamp).getTime()) < 60 * 1000;
                      const hasTreatment = !!r.treatment_version;
                      return (
                      <TableRow key={r.id} data-testid={`consent-row-${r.id}`} className={isFresh ? "bg-emerald-50/40" : ""}>
                        <TableCell className="py-4">
                          <div data-testid={`consent-status-${r.id}`}
                            title={hasTreatment ? "Datenschutz + Behandlungsvertrag unterzeichnet" : "Nur Datenschutz unterzeichnet"}
                            className={`w-9 h-9 rounded-full grid place-items-center ${hasTreatment ? "bg-emerald-600 text-white" : "bg-stone-200 text-stone-500"}`}>
                            <CheckCircle2 className="w-5 h-5" />
                          </div>
                        </TableCell>
                        <TableCell className="py-4 font-medium text-stone-900">
                          {r.patient_name}
                          {isFresh && <span data-testid={`consent-fresh-${r.id}`} className="ml-2 inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-emerald-600 text-white">Neu</span>}
                        </TableCell>
                        <TableCell className="py-4 text-stone-600">{r.patient_email || "—"}</TableCell>
                        <TableCell className="py-4 text-stone-600">{new Date(r.timestamp).toLocaleString("de-DE")}</TableCell>
                        <TableCell className="py-4">
                          <div className="flex items-center gap-2 text-xs">
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-50 text-emerald-900" data-testid={`badge-privacy-${r.id}`}>
                              <ShieldCheck className="w-3.5 h-3.5" /> DS v{r.consent_version}
                            </span>
                            {hasTreatment ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-50 text-emerald-900" data-testid={`badge-treatment-${r.id}`}>
                                <FileSignature className="w-3.5 h-3.5" /> BV v{r.treatment_version}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-stone-100 text-stone-500" title="Kein Behandlungsvertrag unterzeichnet">
                                <FileSignature className="w-3.5 h-3.5" /> BV —
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="py-4 text-right space-x-2">
                          <Button data-testid="admin-download-pdf-button" onClick={() => askDownloadPdf(r.id, r.patient_name, hasTreatment)}
                            className="h-11 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white">
                            <Download className="w-4 h-4 mr-2" /> PDF
                          </Button>
                          <Button data-testid="admin-delete-consent-button" onClick={() => askDeleteConsent(r.id, r.patient_name)}
                            variant="ghost" className="h-11 rounded-xl text-orange-600 hover:bg-orange-50">
                            <Trash className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );})}
                  </TableBody>
                </Table>
              </div>
              <p className="text-xs text-stone-400 mt-6 flex items-center gap-2">
                <span data-testid="consents-count">{rows.length} Einträge</span>
                <span aria-hidden className="inline-block w-1 h-1 rounded-full bg-stone-300" />
                <span title="Aktualisiert automatisch alle 10 Sekunden" className="inline-flex items-center gap-1">
                  <RefreshCw className="w-3 h-3" /> Live-Aktualisierung aktiv
                </span>
              </p>
            </div>
          </TabsContent>

          <TabsContent value="privacy">
            <div className="bg-white rounded-3xl border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 md:p-8">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                  <h1 className="font-display text-2xl font-semibold text-emerald-900">Datenschutzerklärung</h1>
                  <p className="text-sm text-stone-500">Aktuelle Version: <span className="font-medium text-stone-800">{policyVersion || "—"}</span></p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <input ref={policyPdfRef} type="file" accept="application/pdf" className="hidden"
                    data-testid="policy-pdf-input" onChange={(e) => importPolicyPdf(e.target.files?.[0])} />
                  <Button data-testid="policy-pdf-import-button" variant="ghost" disabled={importingPdf}
                    onClick={() => policyPdfRef.current?.click()}
                    className="h-12 rounded-xl text-emerald-900 border border-emerald-200 hover:bg-emerald-50">
                    <FileUp className="mr-2 w-4 h-4" /> {importingPdf ? "Liest PDF…" : "Aus PDF importieren"}
                  </Button>
                  <Button data-testid="save-privacy-button" onClick={savePolicy} disabled={savingPolicy}
                    className="h-12 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white">
                    <Save className="mr-2 w-4 h-4" /> {savingPolicy ? "Speichert…" : "Neue Version speichern"}
                  </Button>
                </div>
              </div>
              <Textarea data-testid="privacy-textarea" value={policyText} onChange={(e) => setPolicyText(e.target.value)}
                className="min-h-[420px] text-base rounded-2xl p-5 leading-relaxed" />
              <p className="text-xs text-stone-400 mt-3">Beim Speichern wird automatisch eine neue Version erzeugt. Bestehende Einwilligungen bleiben mit der jeweils gültigen Version verknüpft. PDF-Import liest nur durchsuchbaren Text — eingescannte PDFs ohne OCR werden nicht erkannt.</p>

              {/* Ja/Nein-Zusatzfragen */}
              <div className="mt-8 pt-6 border-t border-stone-100">
                <div className="flex items-start justify-between flex-wrap gap-3 mb-1">
                  <div>
                    <h2 className="font-display text-xl font-semibold text-emerald-900">Zusätzliche Ja/Nein-Fragen</h2>
                    <p className="text-sm text-stone-500 mt-1 max-w-2xl">
                      Eigene Fragen, die der Patient bei der Datenschutz-Zustimmung mit <strong>Ja</strong> oder <strong>Nein</strong> beantwortet
                      (z. B. „Darf ich Sie per E-Mail zu Terminen kontaktieren?“). Die Antworten werden revisionssicher gespeichert und erscheinen im PDF.
                    </p>
                  </div>
                  <Button data-testid="save-questions-button" onClick={saveQuestions} disabled={savingQuestions}
                    className="h-12 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white">
                    <Save className="mr-2 w-4 h-4" /> {savingQuestions ? "Speichert…" : "Fragen speichern"}
                  </Button>
                </div>

                <div className="space-y-3 mt-4" data-testid="questions-list">
                  {privacyQuestions.length === 0 && (
                    <p className="text-sm text-stone-400 py-3" data-testid="questions-empty">Noch keine Zusatzfragen angelegt.</p>
                  )}
                  {privacyQuestions.map((q, idx) => (
                    <div key={idx} className="flex items-center gap-2" data-testid={`question-row-${idx}`}>
                      <span className="w-8 h-12 grid place-items-center text-sm font-medium text-stone-400 shrink-0">{idx + 1}.</span>
                      <Input data-testid={`question-input-${idx}`} value={q.text}
                        onChange={(e) => updateQuestion(idx, e.target.value)} maxLength={300}
                        placeholder="Fragetext, z. B. „Darf ich Sie per E-Mail kontaktieren?"
                        className="h-12 rounded-xl flex-1" />
                      <span className="hidden sm:flex items-center gap-1 text-xs text-stone-400 px-2 shrink-0">Ja / Nein</span>
                      <Button data-testid={`question-remove-${idx}`} onClick={() => removeQuestion(idx)}
                        variant="ghost" className="h-12 w-12 rounded-xl text-orange-600 hover:bg-orange-50 shrink-0">
                        <Trash className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <Button data-testid="add-question-button" onClick={addQuestion} variant="ghost"
                  className="mt-4 h-12 rounded-xl text-emerald-900 border border-emerald-200 hover:bg-emerald-50">
                  <Plus className="mr-2 w-4 h-4" /> Frage hinzufügen
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="contract">
            <div className="bg-white rounded-3xl border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 md:p-8">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                  <h1 className="font-display text-2xl font-semibold text-emerald-900">Behandlungsvertrag</h1>
                  <p className="text-sm text-stone-500">Aktuelle Version: <span className="font-medium text-stone-800">{contractVersion || "—"}</span> {contractVersion ? "" : "(noch nicht hinterlegt — Patienten unterzeichnen nur Datenschutz)"}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <input ref={contractPdfRef} type="file" accept="application/pdf" className="hidden"
                    data-testid="contract-pdf-input" onChange={(e) => importContractPdf(e.target.files?.[0])} />
                  <Button data-testid="contract-pdf-import-button" variant="ghost" disabled={importingContractPdf}
                    onClick={() => contractPdfRef.current?.click()}
                    className="h-12 rounded-xl text-emerald-900 border border-emerald-200 hover:bg-emerald-50">
                    <FileUp className="mr-2 w-4 h-4" /> {importingContractPdf ? "Liest PDF…" : "Aus PDF importieren"}
                  </Button>
                  <Button data-testid="save-contract-button" onClick={saveContract} disabled={savingContract}
                    className="h-12 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white">
                    <Save className="mr-2 w-4 h-4" /> {savingContract ? "Speichert…" : "Neue Version speichern"}
                  </Button>
                </div>
              </div>
              <Textarea data-testid="contract-textarea" value={contractText} onChange={(e) => setContractText(e.target.value)}
                placeholder="Hier den Behandlungsvertrag eintippen oder per PDF importieren …"
                className="min-h-[420px] text-base rounded-2xl p-5 leading-relaxed" />
              <p className="text-xs text-stone-400 mt-3">
                Wenn ein Behandlungsvertrag hinterlegt ist, müssen Patienten ihn nach dem QR-Scan zusätzlich zur Datenschutzerklärung lesen und unterschreiben. Beide Dokumente landen in einer kombinierten PDF.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="staff">
            <div className="bg-white rounded-3xl border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 md:p-8">
              <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div>
                  <h1 className="font-display text-2xl font-semibold text-emerald-900 flex items-center gap-2">
                    <Users className="w-6 h-6" /> Mitarbeiter
                  </h1>
                  <p className="text-sm text-stone-500 mt-1">
                    Mitarbeiter haben einen eigenen Login. Jede Aktion wird im Verlauf mit dem jeweiligen Account dokumentiert.
                  </p>
                </div>
                <Button data-testid="staff-refresh-button" variant="secondary" onClick={loadStaff}
                  className="h-11 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-900">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-8 p-5 rounded-2xl bg-stone-50 border border-stone-100">
                <div>
                  <Label className="text-sm text-stone-700">Vor- und Nachname</Label>
                  <Input data-testid="staff-new-name" value={newStaff.name}
                    onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })}
                    placeholder="z. B. Anna Müller" className="h-11 rounded-xl mt-1" />
                </div>
                <div>
                  <Label className="text-sm text-stone-700">E-Mail</Label>
                  <Input data-testid="staff-new-email" type="email" value={newStaff.email}
                    onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })}
                    placeholder="name@praxis.de" className="h-11 rounded-xl mt-1" />
                </div>
                <div>
                  <Label className="text-sm text-stone-700">Rolle</Label>
                  <select data-testid="staff-new-role" value={newStaff.role}
                    onChange={(e) => setNewStaff({ ...newStaff, role: e.target.value })}
                    className="w-full h-11 rounded-xl border border-stone-200 bg-white px-3 mt-1">
                    <option value="staff">Mitarbeiter (Empfang/Therapie)</option>
                    <option value="practice_admin">Praxis-Admin</option>
                  </select>
                </div>
                <div>
                  <Label className="text-sm text-stone-700">Initial-Passwort (min. 8 Zeichen)</Label>
                  <Input data-testid="staff-new-password" type="text" value={newStaff.password}
                    onChange={(e) => setNewStaff({ ...newStaff, password: e.target.value })}
                    className="h-11 rounded-xl mt-1 font-mono" />
                </div>
                <div className="md:col-span-2">
                  <Button data-testid="staff-create-button" onClick={createStaff} disabled={savingStaff}
                    className="h-12 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white">
                    <Users className="mr-2 w-4 h-4" /> {savingStaff ? "Speichert…" : "Mitarbeiter anlegen"}
                  </Button>
                </div>
              </div>

              <div className="rounded-2xl border border-stone-100 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-stone-50 hover:bg-stone-50">
                      <TableHead className="py-4 text-stone-600">Name</TableHead>
                      <TableHead className="py-4 text-stone-600">E-Mail</TableHead>
                      <TableHead className="py-4 text-stone-600">Rolle</TableHead>
                      <TableHead className="py-4 text-stone-600">Status</TableHead>
                      <TableHead className="py-4 text-stone-600 text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingStaff && <TableRow><TableCell colSpan={5} className="text-center py-12 text-stone-400">Lädt…</TableCell></TableRow>}
                    {!loadingStaff && staffRows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-12 text-stone-400">Noch keine Mitarbeiter.</TableCell></TableRow>}
                    {staffRows.map((s) => {
                      const me = s.id === user.id;
                      return (
                        <TableRow key={s.id} data-testid={`staff-row-${s.id}`}>
                          <TableCell className="py-4 font-medium text-stone-900">{s.name} {me && <span className="ml-2 text-xs text-emerald-700">(Sie)</span>}</TableCell>
                          <TableCell className="py-4 text-stone-600">{s.email}</TableCell>
                          <TableCell className="py-4">
                            <select value={s.role} disabled={me}
                              onChange={(e) => updateStaffRole(s.id, e.target.value)}
                              data-testid={`staff-role-${s.id}`}
                              className="h-10 rounded-lg border border-stone-200 bg-white px-2 text-sm disabled:opacity-50">
                              <option value="staff">Mitarbeiter</option>
                              <option value="practice_admin">Praxis-Admin</option>
                            </select>
                          </TableCell>
                          <TableCell className="py-4">
                            <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${s.active ? "bg-emerald-50 text-emerald-900" : "bg-stone-200 text-stone-700"}`}>
                              {s.active ? "aktiv" : "inaktiv"}
                            </span>
                          </TableCell>
                          <TableCell className="py-4 text-right space-x-1">
                            <Button data-testid={`staff-reset-password-${s.id}`} variant="ghost" onClick={() => resetStaffPassword(s.id, s.email)}
                              className="h-10 rounded-lg text-stone-700 hover:bg-stone-100" title="Passwort zurücksetzen">
                              <KeyRound className="w-4 h-4" />
                            </Button>
                            <Button data-testid={`staff-toggle-active-${s.id}`} variant="ghost" disabled={me}
                              onClick={() => toggleStaffActive(s.id, !s.active)}
                              className="h-10 rounded-lg text-stone-700 hover:bg-stone-100 disabled:opacity-30"
                              title={s.active ? "Deaktivieren" : "Aktivieren"}>
                              <UserX className="w-4 h-4" />
                            </Button>
                            <Button data-testid={`staff-delete-${s.id}`} variant="ghost" disabled={me}
                              onClick={() => deleteStaff(s.id, s.email)}
                              className="h-10 rounded-lg text-orange-600 hover:bg-orange-50 disabled:opacity-30" title="Entfernen">
                              <Trash className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="settings">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-3xl border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 md:p-8">
                <h2 className="font-display text-xl font-semibold text-emerald-900 mb-4">Praxis-Code</h2>
                <p className="text-sm text-stone-500 mb-4">
                  Mit diesem Code können Sie auf einem Tablet direkt den Patientenmodus starten —
                  einfach beim Login zusätzlich zum Passwort eingeben.
                </p>
                <div className="flex items-center gap-3 p-5 rounded-2xl bg-emerald-50 border border-emerald-100">
                  <span data-testid="practice-code-display" className="font-mono text-2xl tracking-[0.3em] font-semibold text-emerald-900 flex-1 select-all">
                    {practice.code || "—"}
                  </span>
                  <Button data-testid="copy-code-button" variant="ghost" onClick={copyCode}
                    className="h-10 rounded-xl text-emerald-900 hover:bg-emerald-100">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <Button data-testid="regenerate-code-button" onClick={regenerateCode}
                  variant="ghost" className="mt-4 h-11 rounded-xl text-orange-600 hover:bg-orange-50">
                  <Sparkles className="mr-2 w-4 h-4" /> Neuen Code generieren
                </Button>
              </div>

              <div className="bg-white rounded-3xl border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 md:p-8" data-testid="subscription-card">
                <div className="flex items-center gap-2 mb-4">
                  <ShieldCheck className="w-5 h-5 text-emerald-700" />
                  <h2 className="font-display text-xl font-semibold text-emerald-900">Abo &amp; Zahlung</h2>
                </div>
                {isPaying ? (
                  <div data-testid="subscription-active" className="p-5 rounded-2xl bg-emerald-50 border border-emerald-100">
                    <div className="flex items-center gap-2 text-emerald-900 font-medium">
                      <CheckCircle2 className="w-5 h-5 text-emerald-700" /> Abo aktiv — LAIzyDocu Pro
                    </div>
                    <p className="text-sm text-emerald-800 mt-2">
                      {fmtEuro(plan?.monthly_gross ?? 69.99)} € / Monat · jederzeit kündbar.
                    </p>
                    {practice.paid_until && (
                      <p className="text-xs text-emerald-700/80 mt-1">
                        Nächste Abbuchung bzw. bezahlt bis: {new Date(practice.paid_until).toLocaleDateString("de-DE")}
                      </p>
                    )}
                    <p className="text-xs text-emerald-800 mt-3 pt-3 border-t border-emerald-100" data-testid="billing-method-display">
                      Zahlungsart: {practice.billing_method === "directdebit"
                        ? <>SEPA-Lastschrift{practice.sepa_info?.iban_last4 ? ` · IBAN •••• ${practice.sepa_info.iban_last4}` : ""}</>
                        : practice.billing_method === "banktransfer" ? "Überweisung (Rechnung)" : "Karte / Sofortüberweisung"}
                    </p>
                  </div>
                ) : practice.billing_method === "directdebit" ? (
                  <div data-testid="subscription-sepa-pending" className="p-5 rounded-2xl bg-sky-50 border border-sky-100">
                    <div className="flex items-center gap-2 text-sky-900 font-medium">
                      <Clock className="w-5 h-5 text-sky-700" /> Abo gebucht — Lastschrift wird eingezogen
                    </div>
                    <p className="text-sm text-sky-800 mt-2">
                      Ihr SEPA-Mandat{practice.sepa_info?.iban_last4 ? ` (IBAN •••• ${practice.sepa_info.iban_last4})` : ""} ist erteilt.
                      Der Einzug dauert einige Bankarbeitstage – Ihr Zugang ist bereits freigeschaltet.
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-stone-500 mb-4">
                      {practice.trial_ends_at
                        ? <>Sie nutzen LAIzyDocu aktuell im <strong>Testzeitraum</strong>. Wandeln Sie ihn jederzeit in ein vollwertiges Abo um — Ihr QR-Code und alle Funktionen bleiben dauerhaft aktiv.</>
                        : <>Schließen Sie das LAIzyDocu&nbsp;Pro-Abo ab, um alle Funktionen dauerhaft zu nutzen.</>}
                    </p>
                    <div className="p-5 rounded-2xl bg-emerald-900 text-white">
                      <div className="flex items-end gap-2">
                        <span className="font-display text-3xl font-semibold">{fmtEuro(plan?.monthly_gross ?? 69.99)} €</span>
                        <span className="text-emerald-200 mb-1">/ Monat</span>
                      </div>
                      <div className="mt-4 pt-4 border-t border-emerald-700/60 text-sm space-y-1.5">
                        <div className="flex justify-between"><span className="text-emerald-200">Einmalige Einrichtung</span><span>{fmtEuro(plan?.setup_net ?? 189.99)} € netto</span></div>
                        <div className="flex justify-between font-semibold mt-1"><span>Heute fällig</span><span data-testid="subscription-first-payment">{fmtEuro(plan?.first_payment ?? 296.08)} €</span></div>
                        <p className="text-xs text-emerald-300/80 pt-1">Danach automatisch {fmtEuro(plan?.monthly_gross ?? 69.99)} € / Monat. Jederzeit kündbar.</p>
                      </div>
                    </div>
                    <Button data-testid="upgrade-button" onClick={() => navigate("/bezahlen")}
                      className="mt-4 w-full h-12 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white text-base font-medium">
                      <Sparkles className="mr-2 w-4 h-4" /> Jetzt Abo abschließen
                    </Button>
                    <p className="text-xs text-stone-400 mt-2 text-center">Sichere Zahlung über Mollie</p>
                  </>
                )}
                {practice.first_paid_at && practice.billing_method !== "directdebit" && !practice.cancelled_at && (
                  <div className="mt-4 p-4 rounded-2xl bg-sky-50 border border-sky-100" data-testid="sepa-switch-box">
                    <p className="text-sm text-sky-900 font-medium">Bequemer per SEPA-Lastschrift zahlen</p>
                    <p className="text-xs text-sky-800/80 mt-1">Der Beitrag wird automatisch alle 4 Wochen eingezogen – keine Rechnungen mehr überweisen.</p>
                    <Button data-testid="sepa-switch-open" onClick={() => setSepaOpen(true)}
                      className="mt-3 h-10 rounded-xl bg-sky-800 hover:bg-sky-700 text-white">Auf Lastschrift umstellen</Button>
                  </div>
                )}
                <SepaSwitchDialog open={sepaOpen} onOpenChange={setSepaOpen} authHeaders={authHeaders}
                  billingMethod={practice.billing_method} onDone={loadPractice} />
              </div>

              <div className="bg-white rounded-3xl border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 md:p-8">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <h2 className="font-display text-xl font-semibold text-emerald-900 flex items-center gap-2">
                      <QrCode className="w-5 h-5" /> QR-Code für Patienten
                    </h2>
                    <p className="text-sm text-stone-500 mt-1">
                      Patient scannt mit dem Handy → landet direkt im Einwilligungs-Formular dieser Praxis.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col items-center">
                  <div className="p-4 rounded-2xl bg-white border border-stone-200 shadow-sm" data-testid="practice-qr-wrap">
                    {practice.code ? (
                      <QRCodeSVG id="practice-qr-svg" value={qrTargetUrl} size={192} level="M"
                        bgColor="#ffffff" fgColor="#064e3b" includeMargin={false} />
                    ) : (
                      <div className="w-48 h-48 grid place-items-center text-stone-300 text-sm">Kein Code</div>
                    )}
                  </div>
                  <p data-testid="practice-qr-url" className="mt-3 text-xs text-stone-400 break-all text-center max-w-[260px]">
                    {qrTargetUrl || "—"}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2 mt-5 w-full">
                    <Button data-testid="qr-download-button" onClick={downloadQrPng} disabled={!practice.code}
                      className="flex-1 h-11 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white">
                      <Download className="mr-2 w-4 h-4" /> PNG herunterladen
                    </Button>
                    <Button data-testid="qr-print-button" onClick={printQrPoster} disabled={!practice.code}
                      variant="ghost" className="flex-1 h-11 rounded-xl text-emerald-900 border border-emerald-200 hover:bg-emerald-50">
                      <Printer className="mr-2 w-4 h-4" /> Aufsteller drucken
                    </Button>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-3xl border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 md:p-8" data-testid="readability-card">
                <h2 className="font-display text-xl font-semibold text-emerald-900 flex items-center gap-2 mb-1">
                  <Type className="w-5 h-5" /> Lesbarkeit (Patientenansicht)
                </h2>
                <p className="text-sm text-stone-500 mb-5 max-w-md">
                  Legen Sie fest, wie Datenschutzerklärung &amp; Behandlungsvertrag auf dem Smartphone des Patienten dargestellt werden. Das archivierte PDF bleibt im Standard-Layout.
                </p>

                <Label className="text-base font-medium text-stone-800">Schriftgröße</Label>
                <div className="flex gap-2 mt-2" data-testid="fontsize-group">
                  {[["base", "Normal"], ["lg", "Groß"], ["xl", "Sehr groß"]].map(([val, lbl]) => (
                    <button key={val} type="button" data-testid={`fontsize-${val}`}
                      onClick={() => setDisplaySettings((d) => ({ ...d, font_size: val }))}
                      className={`h-11 px-4 rounded-xl text-sm font-medium border transition-colors ${displaySettings.font_size === val ? "bg-emerald-900 text-white border-emerald-900" : "bg-white text-stone-700 border-stone-300 hover:border-emerald-400"}`}>
                      {lbl}
                    </button>
                  ))}
                </div>

                <div className="flex items-center justify-between mt-5 p-4 rounded-2xl bg-stone-50 border border-stone-200">
                  <div>
                    <p className="text-sm font-medium text-stone-800">Blocksatz</p>
                    <p className="text-xs text-stone-500">Text bündig an beiden Rändern ausrichten.</p>
                  </div>
                  <Switch data-testid="justify-switch" checked={displaySettings.justify}
                    onCheckedChange={(v) => setDisplaySettings((d) => ({ ...d, justify: Boolean(v) }))} />
                </div>
                <div className="flex items-center justify-between mt-3 p-4 rounded-2xl bg-stone-50 border border-stone-200">
                  <div>
                    <p className="text-sm font-medium text-stone-800">Silbentrennung</p>
                    <p className="text-xs text-stone-500">Lange Wörter am Zeilenende automatisch trennen.</p>
                  </div>
                  <Switch data-testid="hyphenate-switch" checked={displaySettings.hyphenate}
                    onCheckedChange={(v) => setDisplaySettings((d) => ({ ...d, hyphenate: Boolean(v) }))} />
                </div>

                <div className="flex flex-wrap gap-2 mt-6">
                  <Button data-testid="preview-mobile-button" variant="ghost" onClick={() => setPreviewOpen(true)}
                    className="h-12 rounded-xl text-emerald-900 border border-emerald-200 hover:bg-emerald-50">
                    <Smartphone className="mr-2 w-4 h-4" /> Mobile Vorschau
                  </Button>
                  <Button data-testid="save-display-button" onClick={saveDisplaySettings} disabled={savingDisplay}
                    className="h-12 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white">
                    <Save className="mr-2 w-4 h-4" /> {savingDisplay ? "Speichert…" : "Speichern"}
                  </Button>
                </div>
              </div>

              <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                <DialogContent className="max-w-md" data-testid="mobile-preview-dialog">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Smartphone className="w-5 h-5" /> Mobile Vorschau</DialogTitle>
                    <DialogDescription>So sieht das Dokument auf dem Smartphone des Patienten aus.</DialogDescription>
                  </DialogHeader>
                  <div className="flex gap-2 justify-center mb-1">
                    <button type="button" data-testid="preview-doc-privacy" onClick={() => setPreviewDoc("privacy")}
                      className={`h-9 px-3 rounded-lg text-sm font-medium border ${previewDoc === "privacy" ? "bg-emerald-900 text-white border-emerald-900" : "bg-white text-stone-600 border-stone-300"}`}>Datenschutz</button>
                    <button type="button" data-testid="preview-doc-contract" onClick={() => setPreviewDoc("contract")} disabled={!contractText}
                      className={`h-9 px-3 rounded-lg text-sm font-medium border disabled:opacity-40 ${previewDoc === "contract" ? "bg-emerald-900 text-white border-emerald-900" : "bg-white text-stone-600 border-stone-300"}`}>Vertrag</button>
                  </div>
                  <div className="mx-auto w-[300px] rounded-[2.2rem] border-[10px] border-stone-900 bg-stone-900 shadow-xl">
                    <div className="rounded-[1.4rem] overflow-hidden bg-white">
                      <div className="bg-emerald-900 px-4 py-3 text-white text-sm font-semibold truncate">{practice.name || "Ihre Praxis"}</div>
                      {(() => { const ds = docTextStyle(displaySettings); return (
                        <div lang={ds.lang} style={ds.style}
                          data-testid="mobile-preview-text"
                          className={`h-[420px] overflow-y-auto px-4 py-4 whitespace-pre-wrap break-words text-stone-800 leading-relaxed ${ds.sizeClass}`}>
                          {(previewDoc === "privacy" ? policyText : contractText) || "Noch kein Text hinterlegt."}
                        </div>
                      ); })()}
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              <div className="bg-white rounded-3xl border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 md:p-8">
                <h2 className="font-display text-xl font-semibold text-emerald-900 mb-4">Praxis</h2>
                <Label className="text-base font-medium text-stone-800">Name</Label>
                <Input data-testid="practice-name-input" value={practice.name}
                  onChange={(e) => setPractice({ ...practice, name: e.target.value })}
                  className="h-14 px-4 text-lg rounded-xl mt-2" />
                <Label className="text-base font-medium text-stone-800 mt-4 block">Adresse der Praxis</Label>
                <Textarea data-testid="practice-address-input" value={practice.address}
                  onChange={(e) => setPractice({ ...practice, address: e.target.value })}
                  placeholder={"Musterstraße 1\n60311 Frankfurt am Main"} rows={3}
                  className="text-base rounded-xl mt-2 p-4" />
                <p className="text-xs text-stone-400 mt-2">Name und Adresse erscheinen auf jeder Datenschutz- und Behandlungsvertrags-PDF.</p>
                <Button data-testid="practice-save-button" onClick={savePractice}
                  className="mt-4 h-12 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white">
                  <Save className="mr-2 w-4 h-4" /> Speichern
                </Button>

                <div className="mt-8">
                  <Label className="text-base font-medium text-stone-800">Logo</Label>
                  <div className="mt-3 flex items-center gap-5">
                    <div className="w-24 h-24 rounded-2xl bg-stone-50 border border-stone-200 grid place-items-center overflow-hidden">
                      {logoUrl ? (
                        <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" data-testid="logo-preview" />
                      ) : <ImgIcon className="w-8 h-8 text-stone-300" />}
                    </div>
                    <div className="flex flex-col gap-2">
                      <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden"
                        data-testid="logo-file-input" onChange={(e) => uploadLogo(e.target.files?.[0])} />
                      <Button data-testid="logo-upload-button" onClick={() => fileRef.current?.click()}
                        className="h-11 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white">
                        <Upload className="mr-2 w-4 h-4" /> Hochladen
                      </Button>
                      {practice.logo_filename && (
                        <Button data-testid="logo-remove-button" variant="ghost" onClick={removeLogo}
                          className="h-11 rounded-xl text-orange-600 hover:bg-orange-50">
                          <Trash2 className="mr-2 w-4 h-4" /> Entfernen
                        </Button>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-stone-400 mt-3">PNG/JPG, max. 2 MB. Erscheint auf jeder PDF und im Patientenmodus.</p>
                </div>
              </div>

              <div className="bg-white rounded-3xl border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 md:p-8">
                <h2 className="font-display text-xl font-semibold text-emerald-900 mb-4">Passwort ändern</h2>
                <Label className="text-base font-medium text-stone-800">Aktuelles Passwort</Label>
                <Input data-testid="password-current-input" type="password" value={pwCur}
                  onChange={(e) => setPwCur(e.target.value)} className="h-14 px-4 text-lg rounded-xl mt-2 mb-4" />
                <Label className="text-base font-medium text-stone-800">Neues Passwort (min. 8 Zeichen)</Label>
                <Input data-testid="password-new-input" type="password" value={pwNew}
                  onChange={(e) => setPwNew(e.target.value)} className="h-14 px-4 text-lg rounded-xl mt-2" />
                <Button data-testid="password-change-button" onClick={changePassword}
                  className="mt-4 h-12 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white">
                  <KeyRound className="mr-2 w-4 h-4" /> Passwort ändern
                </Button>
              </div>

              <div data-testid="danger-zone" className="md:col-span-2 bg-white rounded-3xl border border-rose-200/80 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden">
                <div className="flex items-center gap-3 px-6 md:px-8 pt-6 pb-4 border-b border-rose-100 bg-rose-50/50">
                  <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-600 grid place-items-center shrink-0">
                    <AlertTriangle className="w-5 h-5" />
                  </div>
                  <h2 className="font-display text-lg font-semibold text-rose-700">Praxis löschen / Abo beenden</h2>
                </div>
                <div className="p-6 md:p-8">
                {practice.cancelled_at ? (
                  <div className="space-y-5">
                    <p className="text-sm text-stone-700 leading-relaxed">
                      Ihr Abo ist <strong>gekündigt</strong>
                      {practice.export_deadline && (() => {
                        const days = Math.ceil((new Date(practice.export_deadline) - new Date()) / 86400000);
                        return <> – Sie können Ihre Daten noch bis <strong>{new Date(practice.export_deadline).toLocaleDateString("de-DE")}</strong>
                          {days >= 0 ? <> ({days} Tag{days === 1 ? "" : "e"})</> : null} exportieren.</>;
                      })()}
                    </p>
                    <Button data-testid="export-my-data-button" onClick={exportMyData}
                      className="h-12 px-6 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white font-medium shadow-sm transition-colors">
                      <Download className="mr-2 w-4 h-4" /> Alle Daten exportieren (ZIP)
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-5">
                    <p className="text-sm text-stone-600 leading-relaxed">
                      Wenn Sie Ihr Abo beenden, bleibt Ihr Zugang noch <strong>30 Tage</strong> bestehen, damit Sie Ihre
                      Daten exportieren und sichern können. Nach Ablauf dieser Frist wird der Zugang gesperrt.
                      Eine endgültige Löschung aller Daten erfolgt anschließend auf Ihre Weisung.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button data-testid="export-my-data-button" variant="outline" onClick={exportMyData}
                        className="h-12 px-5 rounded-xl border-stone-200 bg-white text-stone-700 hover:bg-stone-50 hover:text-stone-900 font-medium transition-colors">
                        <Download className="mr-2 w-4 h-4" /> Daten zuerst exportieren
                      </Button>
                      <Button data-testid="cancel-practice-button" onClick={cancelMyPractice}
                        className="h-12 px-5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-medium shadow-sm hover:shadow transition-all">
                        <Ban className="mr-2 w-4 h-4" /> Praxis löschen / Abo beenden
                      </Button>
                    </div>
                  </div>
                )}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="audit">
            <div className="bg-white rounded-3xl border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 md:p-8">
              <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-6">
                <div>
                  <h1 className="font-display text-2xl font-semibold text-emerald-900 flex items-center gap-2">
                    <History className="w-6 h-6" /> Verlauf / Audit-Log
                  </h1>
                  <p className="text-sm text-stone-500 mt-1">
                    Wer hat wann was getan? Hash-verkettet, unveränderlich — DSGVO-Nachweis bei Aufsicht.
                  </p>
                </div>
                <div className="flex gap-2 items-center">
                  <select data-testid="audit-filter-select" value={auditFilter}
                    onChange={(e) => { setAuditFilter(e.target.value); loadAudit(e.target.value); }}
                    className="h-12 rounded-xl border border-stone-200 bg-white px-4 text-sm text-stone-800">
                    <option value="">Alle Aktionen</option>
                    <option value="login.success">Login erfolgreich</option>
                    <option value="login.failed">Login fehlgeschlagen</option>
                    <option value="consent.created">Einwilligung erstellt</option>
                    <option value="consent.deleted">Einwilligung gelöscht</option>
                    <option value="consent.pdf.download">PDF heruntergeladen</option>
                    <option value="consents.list">Liste geöffnet</option>
                    <option value="privacy_policy.updated">Datenschutz aktualisiert</option>
                    <option value="privacy_policy.pdf_imported">Datenschutz-PDF importiert</option>
                    <option value="treatment_contract.updated">Behandlungsvertrag aktualisiert</option>
                    <option value="treatment_contract.pdf_imported">Behandlungsvertrag-PDF importiert</option>
                    <option value="kiosk.public.started">Patient via QR gestartet</option>
                    <option value="staff.created">Mitarbeiter angelegt</option>
                    <option value="staff.updated">Mitarbeiter geändert</option>
                    <option value="staff.password_reset">Mitarbeiter-Passwort zurückgesetzt</option>
                    <option value="staff.deleted">Mitarbeiter entfernt</option>
                    <option value="consent.retention_purged">Automatische Aufbewahrungs-Löschung</option>
                  </select>
                  <Button data-testid="audit-refresh-button" variant="secondary" onClick={() => loadAudit(auditFilter)}
                    className="h-12 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-900">
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="rounded-2xl border border-stone-100 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-stone-50 hover:bg-stone-50">
                      <TableHead className="py-4 text-stone-600">Zeitpunkt</TableHead>
                      <TableHead className="py-4 text-stone-600">Akteur</TableHead>
                      <TableHead className="py-4 text-stone-600">Aktion</TableHead>
                      <TableHead className="py-4 text-stone-600">Ziel</TableHead>
                      <TableHead className="py-4 text-stone-600">Details</TableHead>
                      <TableHead className="py-4 text-stone-600">IP</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingAudit && <TableRow><TableCell colSpan={6} className="text-center py-12 text-stone-400">Lädt…</TableCell></TableRow>}
                    {!loadingAudit && auditRows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-12 text-stone-400">Keine Einträge.</TableCell></TableRow>}
                    {auditRows.map((r) => {
                      const actor = r.actor_email || (r.actor_role === "kiosk" ? "Patient (Kiosk)" : r.actor_role === "public" ? "Patient (QR-Scan)" : "—");
                      const meta = r.meta || {};
                      let detail = "";
                      if (r.action === "consent.deleted" && meta.reason) detail = `Begründung: ${meta.reason}`;
                      else if (r.action === "consent.created" && meta.patient_name) detail = `${meta.patient_name} · v${meta.version || "?"}`;
                      else if (r.action === "consents.list") detail = `${meta.count || 0} Einträge${meta.query ? ` · Suche "${meta.query}"` : ""}`;
                      else if (r.action === "privacy_policy.updated" && meta.version) detail = `Neue Version ${meta.version}`;
                      else if (r.action === "privacy_policy.pdf_imported") detail = `${meta.filename || "PDF"} · ${meta.pages || "?"} S. · ${meta.chars || 0} Zeichen`;
                      else if (r.action === "kiosk.public.started") detail = `via ${meta.via || "QR"}`;
                      else if (r.action === "consent.pdf.download" && meta.disclaimer_ack !== undefined) detail = meta.disclaimer_ack ? "Hinweis bestätigt" : "(ohne Hinweis)";
                      else if (r.action?.startsWith("staff.") && meta.target_email) detail = meta.target_email + (meta.changes ? ` · ${Object.keys(meta.changes).join(", ")}` : "");
                      else if (r.action === "staff.created" && meta.email) detail = `${meta.name || meta.email} (${meta.role})`;
                      else if (r.action === "consent.retention_purged") detail = `Aufbewahrungsfrist (${meta.retention_days || 3650} Tage) abgelaufen`;
                      const failed = r.result === "failure";
                      return (
                        <TableRow key={r.id} data-testid={`audit-row-${r.id}`}>
                          <TableCell className="py-3 text-stone-700 font-mono text-xs whitespace-nowrap">
                            {new Date(r.timestamp).toLocaleString("de-DE")}
                          </TableCell>
                          <TableCell className="py-3 text-stone-900 text-sm">{actor}</TableCell>
                          <TableCell className="py-3">
                            <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${failed ? "bg-orange-100 text-orange-800" : "bg-emerald-50 text-emerald-900"}`}>
                              {r.action}
                            </span>
                          </TableCell>
                          <TableCell className="py-3 text-stone-600 font-mono text-xs">{r.object_id ? r.object_id.slice(0, 8) + "…" : "—"}</TableCell>
                          <TableCell className="py-3 text-stone-600 text-xs max-w-xs truncate" title={detail}>{detail || "—"}</TableCell>
                          <TableCell className="py-3 text-stone-500 text-xs">{r.ip || "—"}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <p className="text-xs text-stone-400 mt-6">
                {auditRows.length} Einträge · Einträge sind hash-verkettet (jeder enthält den Hash des Vorgängers) und werden nie verändert oder gelöscht.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="trash">
            <div className="bg-white rounded-3xl border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 md:p-8">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
                <div>
                  <h1 className="font-display text-2xl font-semibold text-emerald-900">Papierkorb</h1>
                  <p className="text-stone-500 mt-1">Gelöschte Einwilligungen können hier wiederhergestellt werden.</p>
                </div>
                <Button data-testid="trash-refresh-button" variant="secondary" onClick={loadTrash}
                  className="h-12 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-900">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>

              <div className="flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-2xl p-4 mb-6">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  Beim Wiederherstellen wird die PDF neu erzeugt und die Einwilligung erscheint wieder in der Liste.
                  Auf Patientenwunsch gelöschte Einträge (Art. 17 DSGVO) sollten nur in begründeten Ausnahmefällen wiederhergestellt werden.
                </p>
              </div>

              <div className="rounded-2xl border border-stone-100 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-stone-50 hover:bg-stone-50">
                      <TableHead className="py-4 text-stone-600">Name</TableHead>
                      <TableHead className="py-4 text-stone-600">Gelöscht am</TableHead>
                      <TableHead className="py-4 text-stone-600">Grund</TableHead>
                      <TableHead className="py-4 text-stone-600">Kategorie</TableHead>
                      <TableHead className="py-4 text-stone-600 text-right">Aktion</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingTrash && <TableRow><TableCell colSpan={5} className="text-center py-12 text-stone-400">Lädt…</TableCell></TableRow>}
                    {!loadingTrash && trashRows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-12 text-stone-400">Der Papierkorb ist leer.</TableCell></TableRow>}
                    {trashRows.map((r) => (
                      <TableRow key={r.id} data-testid={`trash-row-${r.id}`}>
                        <TableCell className="py-4 font-medium text-stone-900">{r.patient_name}</TableCell>
                        <TableCell className="py-4 text-stone-600">{r.deleted_at ? new Date(r.deleted_at).toLocaleString("de-DE") : "—"}</TableCell>
                        <TableCell className="py-4 text-stone-600 max-w-xs truncate" title={r.deletion_reason || ""}>{r.deletion_reason || "—"}</TableCell>
                        <TableCell className="py-4">
                          <span className={`px-2 py-1 rounded-md text-xs font-medium ${r.deletion_category === "patient_request" ? "bg-orange-100 text-orange-700" : r.deletion_category === "retention_expired" ? "bg-stone-100 text-stone-600" : "bg-amber-50 text-amber-800"}`}>
                            {r.deletion_category === "patient_request" ? "Patientenwunsch" : r.deletion_category === "retention_expired" ? "Aufbewahrung abgelaufen" : "Sonstige"}
                          </span>
                        </TableCell>
                        <TableCell className="py-4 text-right">
                          <Button data-testid={`trash-restore-${r.id}`} onClick={() => restoreConsent(r.id, r.patient_name)}
                            className="h-11 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white">
                            <RefreshCw className="w-4 h-4 mr-2" /> Wiederherstellen
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="text-xs text-stone-400 mt-6" data-testid="trash-count">{trashRows.length} Einträge im Papierkorb</p>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* PDF-Download Disclaimer Dialog */}
      <Dialog open={pdfDialog.open} onOpenChange={(o) => !o && setPdfDialog({ open: false, id: null, name: "", hasTreatment: false })}>
        <DialogContent data-testid="pdf-disclaimer-dialog" className="max-w-md">
          <DialogHeader>
            <div className="w-12 h-12 rounded-xl bg-orange-100 text-orange-700 grid place-items-center mb-3">
              <ShieldAlert className="w-6 h-6" />
            </div>
            <DialogTitle>Personenbezogene Daten</DialogTitle>
            <DialogDescription className="pt-2 text-stone-700 leading-relaxed">
              Sie laden Dokumente von <strong>{pdfDialog.name}</strong> als PDF herunter — mit Gesundheits- und Identifikationsdaten.<br /><br />
              <strong>Bitte beachten:</strong> Nicht an Dritte weitergeben, nur auf freigegebenen Praxis-Geräten speichern, temporäre Kopien sicher löschen.
              {pdfDialog.hasTreatment ? <><br /><br />Wählen Sie aus, welches Dokument geladen werden soll:</> : null}
            </DialogDescription>
          </DialogHeader>
          {pdfDialog.hasTreatment ? (
            <DialogFooter className="gap-2 flex-col sm:flex-col">
              <Button data-testid="pdf-confirm-privacy-button" onClick={() => confirmDownloadPdf("privacy")}
                className="h-12 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white w-full justify-start">
                <Download className="mr-2 w-4 h-4" /> Datenschutzerklärung
              </Button>
              <Button data-testid="pdf-confirm-treatment-button" onClick={() => confirmDownloadPdf("treatment")}
                className="h-12 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white w-full justify-start">
                <Download className="mr-2 w-4 h-4" /> Behandlungsvertrag
              </Button>
              <Button data-testid="pdf-confirm-combined-button" onClick={() => confirmDownloadPdf("combined")}
                variant="ghost" className="h-12 rounded-xl text-emerald-900 border border-emerald-200 hover:bg-emerald-50 w-full justify-start">
                <Download className="mr-2 w-4 h-4" /> Beide kombiniert (1 PDF)
              </Button>
              <Button data-testid="pdf-cancel-button" variant="ghost"
                onClick={() => setPdfDialog({ open: false, id: null, name: "", hasTreatment: false })}
                className="h-12 rounded-xl">Abbrechen</Button>
            </DialogFooter>
          ) : (
            <DialogFooter className="gap-2">
              <Button data-testid="pdf-cancel-button" variant="ghost"
                onClick={() => setPdfDialog({ open: false, id: null, name: "", hasTreatment: false })}
                className="h-12 rounded-xl">Abbrechen</Button>
              <Button data-testid="pdf-confirm-button" onClick={() => confirmDownloadPdf("combined")}
                className="h-12 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white">
                <Download className="mr-2 w-4 h-4" /> Verstanden — jetzt herunterladen
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete consent Dialog */}
      <Dialog open={delDialog.open} onOpenChange={(o) => !o && setDelDialog({ ...delDialog, open: false })}>
        <DialogContent data-testid="delete-consent-dialog" className="max-w-lg">
          <DialogHeader>
            <div className="w-12 h-12 rounded-xl bg-orange-100 text-orange-700 grid place-items-center mb-3">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <DialogTitle>Einwilligung löschen</DialogTitle>
            <DialogDescription className="pt-2 text-stone-700 leading-relaxed">
              Sie löschen die Einwilligung von <strong>{delDialog.name}</strong>. Einwilligungen unterliegen einer
              gesetzlichen Aufbewahrungsfrist von <strong>10 Jahren</strong> (analog § 630f BGB).
              Eine vorzeitige Löschung ist nur zulässig, wenn der Patient dies ausdrücklich verlangt (Art. 17 DSGVO).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-sm text-stone-700">Lösch-Grund</Label>
              <select data-testid="delete-category-select" value={delDialog.category}
                onChange={(e) => setDelDialog({ ...delDialog, category: e.target.value })}
                className="w-full h-11 rounded-xl border border-stone-200 bg-white px-3 mt-1">
                <option value="patient_request">Patient verlangt Löschung (Art. 17 DSGVO)</option>
                <option value="other">Sonstiger Grund (nur nach Ablauf der 10-Jahres-Frist)</option>
              </select>
            </div>
            <div>
              <Label className="text-sm text-stone-700">Begründung (für das Audit-Protokoll, min. 3 Zeichen)</Label>
              <Textarea data-testid="delete-reason-textarea" value={delDialog.reason}
                onChange={(e) => setDelDialog({ ...delDialog, reason: e.target.value })}
                placeholder="z. B. Patient hat schriftlich Löschung verlangt am 02.06.2026"
                className="min-h-[100px] rounded-xl mt-1" />
            </div>
          </div>
          <DialogFooter className="gap-2 mt-2">
            <Button data-testid="delete-cancel-button" variant="ghost"
              onClick={() => setDelDialog({ ...delDialog, open: false })}
              className="h-12 rounded-xl">Abbrechen</Button>
            <Button data-testid="delete-confirm-button" onClick={confirmDeleteConsent} disabled={deleting}
              className="h-12 rounded-xl bg-orange-700 hover:bg-orange-800 text-white">
              <Trash className="mr-2 w-4 h-4" /> {deleting ? "Löscht…" : "Löschen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
